import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { SGE_MODULE_NAME, USERNAME } from "constants/api/localStorage";
import {
  baseUrlApplicat,
  LOGIN,
  LOGOUT,
  RENEW_URL,
  SGE_MODULE,
  VALIDATION_URL,
} from "constants/api/routeGlobals";
import { Login } from "models/Resources/Login";
import { LoginResponse } from "models/Responses/LoginResponse";
import {
  RenewResponse,
  TokenValidityResponse,
} from "models/Responses/TokenResponse";
import { UserAuthorizations } from "models/UserAuthorization";
import { loginResponseToLoginResource } from "services/responseConverters/loginResponseToLoginResource";
import {
  addLoadingRequest,
  removeLoadingRequest,
  setd_inv22g,
  setToken,
} from "store/Slices/appStateSlice";

export const connectionApi = createApi({
  reducerPath: "connectionApi",
  baseQuery: fetchBaseQuery({
    baseUrl: baseUrlApplicat,
    prepareHeaders: Headers => {
      Headers.set("x-applicat-apikey", process.env.REACT_APP_API_KEY!);
      return Headers;
    },
  }),
  endpoints: builder => ({
    login: builder.mutation<any, any>({
      queryFn: async (
        body: any,
        queryApi,
        _extraOptions,
        fetchWithBaseQuery
      ) => {
        try {
          queryApi.dispatch(addLoadingRequest("connection"));
          const response = await fetchWithBaseQuery({
            url: `${LOGIN}?${SGE_MODULE}`,
            method: "POST",
            body: body,
          });

          if (response.data) {
            const login = loginResponseToLoginResource(
              response.data as LoginResponse
            );
            const authorizations = login.authorizations;

            const d_inv22g =
              authorizations.find(
                (a: UserAuthorizations) => a.functName === "gestion_ent"
              ) ?? {};
            queryApi.dispatch(setd_inv22g(d_inv22g));

            if (login.success) {
              queryApi.dispatch(setToken(login.token));
              localStorage.setItem(USERNAME, login.name);
            }
          }
          queryApi.dispatch(removeLoadingRequest("connection"));
          return response;
        } catch (error: any) {
          queryApi.dispatch(removeLoadingRequest("connection"));
          return { error };
        }
      },
    }),

    logInWithToken: builder.query<any, string>({
      queryFn: async (token, _queryApi, _extraOptions, fetchWithBaseQuery) => {
        try {
          const response = await fetchWithBaseQuery({
            url: `${LOGIN}/${token}?modules=${SGE_MODULE_NAME}`,
            method: "GET",
          });
          return {
            data: loginResponseToLoginResource(response.data as LoginResponse),
          };
        } catch (error: any) {
          const wrongLogin = { success: false } as Login;
          return { data: wrongLogin };
        }
      },
    }),

    logout: builder.query({
      queryFn: async (_token, _api, _extraOptions, fetchWithBaseQuery) => {
        try {
          await fetchWithBaseQuery({
            url: `${LOGOUT}`,
            method: "GET",
          });
          return;
        } catch (error: any) {
          return error;
        }
      },
    }),

    getTokenValidity: builder.query<boolean, string>({
      query: token => ({
        url: VALIDATION_URL,
        method: "GET",
        headers: {
          authorization: `Bearer ${token}`,
        },
      }),
      transformResponse: (response: TokenValidityResponse) => response.valid,
      transformErrorResponse: _ => false,
    }),
    resetTokenValidity: builder.query<string, string>({
      query: token => ({
        url: RENEW_URL,
        method: "GET",
        headers: {
          authorization: `Bearer ${token}`,
        },
      }),
      transformResponse: (response: RenewResponse) => response.newToken,
    }),
  }),
});

export const {
  useLoginMutation,
  useLazyLogInWithTokenQuery,
  useLazyLogoutQuery,
  useGetTokenValidityQuery,
  useResetTokenValidityQuery,
} = connectionApi;
